import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import DataTable from '../../../components/DataTable/DataTable';
import { IColInfo } from "../../../components/DataTable/Interfaces";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { ApplicationFunctions, useAuth } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemModel from "../../../models/ItemModel";
import ItemSizeSetModel from "../../../models/ItemSizeSetModel";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { Result } from "../../../types/Result";

interface IProps extends PopupProps {
    item: ItemModel
    itemRevisionId: number
}

const ItemSizeSetsPopup: React.FC<IProps> = ({ show, onHide, item, itemRevisionId }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [tableData, setTableData] = useState<ItemSizeSetModel[]>();
    const [showAddSizeSet, setShowAddSizeSet] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();

    const sizeSets = useSelectListOptions('api/v1/selectlist/SizeSets?activeOnly=true', '-- Select sizeset');

    const [formData, setFormData] = useState<ItemSizeSetModel>({
        itemId: item.id,
        itemRevisionId: itemRevisionId,
        sizeSetId: 0
    } as ItemSizeSetModel);


    useEffect(() => {
        getTableData();
    }, [item]);

    function getTableData() {
        DataService.get<any[]>('api/v1/ItemSizeSets/ByItemRevision/' + itemRevisionId)
            .then(response => {
                setTableData(response);
            })
            .catch(e => { handleException(e); });
    }

    function deleteRow(rowData) {
        DataService.delete('api/v1/ItemSizeSets/', rowData.id)
            .then(response => {
                handleResult(response);
                getTableData();
            })
            .catch(e => { handleException(e); });
    }

    function submit() {
        if (formData) {
            DataService
                .create<number>(`api/v1/ItemSizeSets`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        setShowAddSizeSet(false);
                        getTableData();
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'sizeSetId', label: 'Sizeset', labelSize: 2, validationRules: [{ type: 'isRequired' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'Id', visible: false },
        { title: 'Size set', dataProp: 'sizeSet.name', sort: 'asc' }
    ];

    if (isAuthorizedFor(ApplicationFunctions.UpdateItemRevision)) {
        colInfos.push(
            {
                headerButtons: [{ text: "Add", icon: 'fa-plus-square', color: '#26A69A', onClick: () => setShowAddSizeSet(true) }],
                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow }],
            });
    }

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={showAddSizeSet == true ? 'Add size set' : 'Size sets'} >
            <Popup.Body>
                {showAddSizeSet == true && <SelectListField {...getFieldProps('sizeSetId')} options={sizeSets} />}
                {showAddSizeSet == false &&
                    <DataTable
                        name='dtItemSizeSets'
                        tableData={tableData}
                        colInfos={colInfos}
                    />
                }
            </Popup.Body>
            <Popup.Footer>
                {showAddSizeSet && <SaveButton {...getSubmitButtonProps()} text='Add' />}
            </Popup.Footer>
        </Popup>
    );
}
export default ItemSizeSetsPopup;